import type { FC } from 'react';

import { useRouter } from 'next/compat/router';
import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import aa from 'search-insights';
import styled, { css } from 'styled-components';

import { HybrisCmsLink } from '../../core/link/HybrisCmsLink';
import { Typography } from '../../core/typography/Typography';
import {
  CART_ENTRIES,
  LOGGED_IN_STATUS,
  RECENTLY_VIEWED_PRODUCTS,
} from '~/utilities/constants/localStorageKeys';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import type { LinkTarget } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles, storageAvailable } from '~/utilities/helpers';

const S = {
  SideNavLink: styled(HybrisCmsLink)`
    display: flex;
    position: relative;
    width: 100%;
  `,

  LinkTitle: styled(Typography)`
    ${({ theme }) => css`
      display: block;
      position: relative;
      width: 100%;
      font-family: ${theme.font.family.primary};
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.regular};
      text-decoration: none;
      text-transform: capitalize;
      padding: ${theme.padding['2xs']} ${theme.padding.sm};
      line-height: 16px;
      color: ${theme.colors.content.secondary};

      &[data-is-highlighted='true'] {
        color: ${theme.colors.content.primary};
        font-weight: ${theme.variables.font.weight.medium};
      }

      &:hover,
      &:focus {
        color: ${theme.colors.content.hyperlink};
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Outlet]: css`
          &:hover,
          &:focus {
            color: ${theme.colors.orange[400]};

            &[data-is-highlighted='true'] {
              color: ${theme.colors.content.primary};
            }
          }
        `,
        [SiteType.Aaf]: css`
          font-weight: inherit;
          line-height: 19px;

          &[data-is-highlighted='true'] {
            font-weight: inherit;
          }

          &[data-is-highlighted='false'] {
            color: ${theme.colors.content.secondary};

            &:hover,
            &:focus {
              color: ${theme.colors.content.hyperlink};
            }
          }
        `,
      })}
    `}
  `,
};

interface Props {
  url: string;
  label?: string | null;
  classNames?: string;
  dataNavItem?: string;
  parentActive: boolean;
  target?: LinkTarget | null;
  styleObject?: { [key: string]: string };
  isActiveURL: boolean;
  onClick?: () => void;
}

const SideNavLinkComponent: FC<Props> = ({
  styleObject,
  url,
  label,
  classNames,
  dataNavItem,
  target,
  parentActive,
  isActiveURL,
  onClick,
}) => {
  const { isLargeDesktop } = useMediaQueryContext();
  const router = useRouter();

  const { closeSideNav } = useNavActionContext();

  const cleanOnLogout = useCallback(() => {
    if (storageAvailable('localStorage')) {
      Object.keys(localStorage)
        .filter(
          (key: string) =>
            key.startsWith(CART_ENTRIES) ||
            key.endsWith(RECENTLY_VIEWED_PRODUCTS) ||
            key === LOGGED_IN_STATUS
        )
        .forEach((key: string) => localStorage.removeItem(key));
    }

    aa('setAuthenticatedUserToken', undefined);
  }, []);

  const onSideNavLinkClick = useCallback(() => {
    onClick?.();

    if (url === '/logout') {
      cleanOnLogout();
    }

    if (!isLargeDesktop) {
      const handleRouteChange = () => {
        requestAnimationFrame(() => {
          closeSideNav();
        });
        router?.events.off('routeChangeComplete', handleRouteChange);
      };

      router?.events.on('routeChangeComplete', handleRouteChange);
    }
  }, [cleanOnLogout, closeSideNav, isLargeDesktop, onClick, router?.events, url]);

  return (
    <S.SideNavLink
      href={url}
      onClick={onSideNavLinkClick}
      data-nav-item={dataNavItem}
      data-title={label ?? ''}
      className={classNames}
      tabIndex={parentActive ? undefined : -1}
      target={target}
      data-testid={url === '/logout' ? 'sidenav-logout' : undefined}
    >
      <S.LinkTitle
        data-is-highlighted={isActiveURL}
        dangerouslySetInnerHTML={{ __html: label }}
        data-title={label ?? ''}
        style={styleObject}
      />
    </S.SideNavLink>
  );
};

export const SideNavLink = memo(SideNavLinkComponent, isEqual);
